import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import { toast } from 'react-toastify';
import { mockApi } from '../../mock/api';
import {
  PageHeader,
  DataTable,
  StatusBadge,
  ActionMenu,
  ConfirmDialog,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { formatDate } from '../../utils';
import type { CmsPage, FaqItem, PublishStatus } from '../../types';

const CmsPageView: React.FC = () => {
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<FaqItem | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [status, setStatus] = useState<PublishStatus>('Draft');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadPages = async () => {
    setLoadingPages(true);
    try {
      setPages(await mockApi.getCmsPages());
    } catch {
      toast.error('Failed to load pages');
    } finally {
      setLoadingPages(false);
    }
  };

  const loadFaqs = async () => {
    setLoadingFaqs(true);
    try {
      setFaqs(await mockApi.getFaqs());
    } catch {
      toast.error('Failed to load FAQs');
    } finally {
      setLoadingFaqs(false);
    }
  };

  useEffect(() => {
    loadPages();
    loadFaqs();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setQuestion('');
    setAnswer('');
    setStatus('Draft');
    setDialogOpen(true);
  };

  const openEdit = (faq: FaqItem) => {
    setEditing(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setStatus(faq.status);
    setDialogOpen(true);
  };

  const handleSaveFaq = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    setSaving(true);
    try {
      await mockApi.saveFaq({
        id: editing?.id,
        question: question.trim(),
        answer: answer.trim(),
        status,
      });
      toast.success(editing ? 'FAQ updated' : 'FAQ created');
      setDialogOpen(false);
      await loadFaqs();
    } catch {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (faq: FaqItem) => {
    const next: PublishStatus = faq.status === 'Published' ? 'Unpublished' : 'Published';
    try {
      await mockApi.saveFaq({ id: faq.id, status: next });
      toast.success(next === 'Published' ? 'FAQ published' : 'FAQ unpublished');
      await loadFaqs();
    } catch {
      toast.error('Failed to update FAQ');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setSaving(true);
    try {
      await mockApi.deleteFaq(deleteId);
      toast.success('FAQ deleted');
      setDeleteId(null);
      await loadFaqs();
    } catch {
      toast.error('Failed to delete FAQ');
    } finally {
      setSaving(false);
    }
  };

  const pageColumns: Column<CmsPage>[] = [
    {
      id: 'title',
      label: 'Page',
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            /{row.slug}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'updatedAt',
      label: 'Updated',
      render: (row) => formatDate(row.updatedAt),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          component={RouterLink}
          to={`/cms/pages/${row.id}`}
          size="small"
          startIcon={<EditOutlinedIcon />}
        >
          Edit
        </Button>
      ),
    },
  ];

  const faqColumns: Column<FaqItem>[] = [
    {
      id: 'question',
      label: 'Question',
      minWidth: 220,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.question}
        </Typography>
      ),
    },
    {
      id: 'answer',
      label: 'Answer',
      minWidth: 240,
      render: (row) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.answer}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'updatedAt',
      label: 'Updated',
      render: (row) => formatDate(row.updatedAt),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'Edit',
              icon: <EditOutlinedIcon fontSize="small" />,
              onClick: () => openEdit(row),
            },
            {
              label: row.status === 'Published' ? 'Unpublish' : 'Publish',
              icon:
                row.status === 'Published' ? (
                  <UnpublishedOutlinedIcon fontSize="small" />
                ) : (
                  <PublishOutlinedIcon fontSize="small" />
                ),
              onClick: () => togglePublish(row),
            },
            {
              label: 'Delete',
              icon: <DeleteOutlineIcon fontSize="small" />,
              danger: true,
              onClick: () => setDeleteId(row.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="CMS" subtitle="Manage static pages and FAQs" />

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Static Pages
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
        <CardContent>
          <DataTable
            columns={pageColumns}
            rows={pages}
            loading={loadingPages}
            getRowId={(row) => row.id}
            emptyTitle="No CMS pages"
          />
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant="h3">FAQs</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Create FAQ
        </Button>
      </Box>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <DataTable
            columns={faqColumns}
            rows={faqs}
            loading={loadingFaqs}
            getRowId={(row) => row.id}
            emptyTitle="No FAQs yet"
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit FAQ' : 'Create FAQ'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Question"
              fullWidth
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <TextField
              label="Answer"
              fullWidth
              multiline
              minRows={4}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as PublishStatus)}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
                <MenuItem value="Unpublished">Unpublished</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveFaq} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="Delete FAQ"
        message="This FAQ will be permanently removed. Continue?"
        confirmLabel="Delete"
        danger
        loading={saving}
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default CmsPageView;
